import { Grid } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-final-form';
import _ from 'lodash';

import { fetchTratamentosTributariosAdicao } from '../../../features/declaracao-importacao/declaracaoImportacaoAPI';

import QCXSelectManyFundamentosOpcionais from './components/QCXSelectManyFundamentosOpcionais';
import QCXSelectManyFundamentosObrigatorios from './components/QCXSelectManyFundamentosObrigatorios';
import QCXFormSubtitle from '../../form-title/QCXFormSubtitle';
import QCXAtributosTratamentoTributarioDuimpV3 from '../../../shared-components/atributos-tratamento-tributario-duimp/QCXAtributosTratamentoTributarioDuimpV3';

export default function TributosAdicaoDuimpForm({ values, disabled, idFatura }) {
  const form = useForm();
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const [tributosRaw, setTributosRaw] = useState([]);

  const [fundamentosObrigatorios, setFundamentosObrigatorios] = useState([]);
  const [fundamentosOpcionaisGrouped, setFundamentosOpcionaisGrouped] = useState([]);

  const agruparFundamentos = (fundamentosMetadadosArr) => {
    if (!fundamentosMetadadosArr) return [];

    const fundamentos = {};

    fundamentosMetadadosArr.forEach((item) => {
      const codigoFundamento = item.fundamentoLegal.codigo;
      const nomeFundamento = item.fundamentoLegal.nome;
      const tipoFundamento = item.fundamentoLegal.tipo;

      if (!fundamentos[codigoFundamento]) {
        fundamentos[codigoFundamento] = {
          id: codigoFundamento,
          nome: nomeFundamento,
          tipo: tipoFundamento,
          tratamentos: [],
        };
      }

      fundamentos[codigoFundamento].tratamentos.push({
        regime: {
          codigo: item?.regime?.codigo,
          nome: item?.regime?.nome,
        },
        tributo: {
          codigo: item?.tributo?.codigo,
          nome: item?.tributo?.nome,
        },
      });
    });

    const fundamentosParsed = Object.values(fundamentos).map((fundamento) => ({ ...fundamento }));
    const fundamentosOrdenados = fundamentosParsed.sort((a, b) => a.id.localeCompare(b.id));

    return fundamentosOrdenados;
  };

  const obterCodigoTratamento = useCallback((tratamento) => {
    const fundamentoCodigo =
      tratamento?.fundamentoLegal?.codigo ?? tratamento?.fundamento?.id ?? tratamento?.fundamento?.codigo;
    return `${tratamento?.tributo?.codigo};${tratamento?.regime?.codigo};${fundamentoCodigo}`;
  }, []);

  useEffect(() => {
    const adicaoId = values?.id;

    if (adicaoId) {
      fetchTratamentosTributariosAdicao(adicaoId)
        .then((response) => {
          const responseData = response?.data || {};
          setTributosRaw(responseData);
          const tratamentosObrigatorios = _.isArray(responseData?.tratamentosTributarios)
            ? responseData.tratamentosTributarios
            : [];
          const tratamentosOpcionaisDisponiveis = _.isArray(responseData?.fundamentosOpcionaisDisponiveis)
            ? responseData.fundamentosOpcionaisDisponiveis
            : [];

          if (tratamentosOpcionaisDisponiveis.length) {
            const fundamentosOrdenados = agruparFundamentos(tratamentosOpcionaisDisponiveis);
            setFundamentosOpcionaisGrouped(fundamentosOrdenados);
          } else {
            setFundamentosOpcionaisGrouped([]);
          }

          if (tratamentosObrigatorios.length) {
            // agrupa fundamentos obrigatorios para exibicao
            const fundamentosOrdenados = agruparFundamentos(tratamentosObrigatorios);
            setFundamentosObrigatorios(fundamentosOrdenados);
          } else {
            setFundamentosObrigatorios([]);
          }

          const valoresMetadadosSalvos = _.get(valuesRef.current, 'valoresMetadadoTtce.valorMetadadosJson', {});
          const valoresPorCodigo = {};

          ['obrigatorios', 'opcionais'].forEach((grupo) => {
            Object.values(valoresMetadadosSalvos?.[grupo] || {}).forEach((tratamento) => {
              (tratamento?.atributos || []).forEach((atributo) => {
                valoresPorCodigo[atributo.codigo] = atributo.valor;
              });
            });
          });

          const atributosValores = {};
          [...tratamentosObrigatorios, ...tratamentosOpcionaisDisponiveis].forEach((tratamento) => {
            const codigoTratamento = obterCodigoTratamento(tratamento);

            (tratamento?.atributos || []).forEach((atributo) => {
              if (!atributo?.codigo) return;

              const atributoAtual = atributosValores[atributo.codigo];
              const codTratamentosPossiveis = atributoAtual?.codTratamentosPossiveis || [];

              atributosValores[atributo.codigo] = {
                ...atributoAtual,
                codigo: atributo.codigo,
                valor: Object.prototype.hasOwnProperty.call(valoresPorCodigo, atributo.codigo)
                  ? valoresPorCodigo[atributo.codigo]
                  : atributoAtual?.valor ?? atributo?.valor ?? '',
                codTratamentosPossiveis: [...new Set([...codTratamentosPossiveis, codigoTratamento])],
              };
            });
          });

          form.change('ignorableFields.tratamentosTtce.metadados.valores', atributosValores);
        })
        .catch((error) => {
          console.error(error);
          setTributosRaw([]);
          setFundamentosObrigatorios([]);
          setFundamentosOpcionaisGrouped([]);
        });
    } else {
      setTributosRaw([]);
      setFundamentosObrigatorios([]);
      setFundamentosOpcionaisGrouped([]);
    }
  }, [form, obterCodigoTratamento, values?.id]);

  const tributosFiltradosRaw = useMemo(() => {
    const tratamentosObrigatorios = Array.isArray(tributosRaw?.tratamentosTributarios)
      ? tributosRaw.tratamentosTributarios
      : [];
    const tratamentosOpcionaisDisponiveis = Array.isArray(tributosRaw?.fundamentosOpcionaisDisponiveis)
      ? tributosRaw.fundamentosOpcionaisDisponiveis
      : [];
    const tratamentosObrigatoriosSelecionados = _.get(
      values,
      'ignorableFields.tratamentosTtce.metadados.obrigatorios',
      []
    );
    const tratamentosOpcionaisSelecionados = _.get(values, 'ignorableFields.tratamentosTtce.metadados.opcionais', []);
    const codigosTratamentosSelecionados = new Set(
      [...tratamentosObrigatoriosSelecionados, ...tratamentosOpcionaisSelecionados].map(obterCodigoTratamento)
    );

    return [...tratamentosObrigatorios, ...tratamentosOpcionaisDisponiveis].filter((tratamento) =>
      codigosTratamentosSelecionados.has(obterCodigoTratamento(tratamento))
    );
  }, [tributosRaw, values, obterCodigoTratamento]);

  const tributosFiltradosV3 = useMemo(
    () =>
      tributosFiltradosRaw.map((tratamento, index) =>
        tratamento?.tratamentos
          ? tratamento
          : {
              chave: index,
              tratamentos: tratamento,
            }
      ),
    [tributosFiltradosRaw]
  );

  return (
    <>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <QCXFormSubtitle title="Tratamentos Opcionais" />
      </Grid>

      <QCXSelectManyFundamentosOpcionais
        title="Fundamentos Opcionais"
        name="ignorableFields.tratamentosTtce.metadados.opcionais"
        values={values}
        disabled={disabled}
        configureFilterPredicate={() => true}
        fundamentosOpcionaisGrouped={fundamentosOpcionaisGrouped}
        idFatura={idFatura}
      />

      <Grid item xs={12} sm={12} md={12} lg={12}>
        <QCXFormSubtitle title="Tratamentos Obrigatorios" />
      </Grid>

      <QCXSelectManyFundamentosObrigatorios
        title="Fundamentos Obrigatorios"
        name="ignorableFields.tratamentosTtce.metadados.obrigatorios"
        values={values}
        disabled={disabled}
        configureFilterPredicate={() => true}
        fundamentosGrouped={fundamentosObrigatorios}
      />

      <>
        <QCXAtributosTratamentoTributarioDuimpV3
          name="ignorableFields.tratamentosTtce.metadados.valores"
          tributos={tributosFiltradosV3}
          isConsult={disabled}
        />
      </>
    </>
  );
}
